import { UI, HStack, Stack, VStack } from '@genial/components';
import { BoxRadius, Typography } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import { Box } from 'native-base';
import React from 'react';

import {
  TableContent,
  TableContentItemText,
} from '../../../../../../../../../components/TableContent';
import { useGlobalAssetContext } from '../../../../../contexts';
import { useEquitySellAssetController } from '../../../../../hooks/products/EquitySell';
import { AssetTitleWithImage } from '../../../components/AssetTitleWithImage';
import { AssetValueWithPercentage } from '../../../components/AssetValueWithPercentage';
import { LateralBar } from '../../../components/LateralBar';
import { ListLoadingItemTablet } from '../../Equity/ListLoadingItem/tablet';

import { EquityListItemTypes } from '.';

export const EquityListItemTablet: React.FC<EquityListItemTypes.Props> = ({ offer }) => {
  const id = offer.quote.productId;
  const {
    amountItem,
    priceItem,
    totalPrice,
    handleChangeAmountItem,
    handleChangePriceItem,
    totalPriceIsInvalid,
    cautionStepper,
    fractionalLotSize,
    isFocusedAmountInput,
    setIsFocusedAmountInput,
    availableAmount,
  } = useEquitySellAssetController(offer);
  const { infoGetter, selectedTab } = useGlobalAssetContext();

  const completeOffer = { ...offer, orderQuantity: amountItem, orderValue: priceItem };

  const { Button, isSelected, forceLoading } = infoGetter(
    'equity',
    completeOffer,
    'productIdNormalized'
  );

  if (forceLoading) {
    return <ListLoadingItemTablet />;
  }

  return (
    <TableContent
      direction="column"
      space="stack.xxxs"
      p={0}
      px="inline.xxxs"
      py="stack.xxxs"
      isHighlighted={isSelected}>
      <LateralBar
        backgroundColor={selectedTab.color}
        borderTopLeftRadius={BoxRadius.sm}
        borderBottomLeftRadius={BoxRadius.sm}
      />
      <HStack justifyContent="space-between" w="full">
        <Stack w="full">
          <AssetTitleWithImage
            image={offer.imageNormalized || ''}
            title={offer.productNameNormalized || ''}
            subtitle={offer.symbolNormalized || ''}
          />
        </Stack>
      </HStack>
      <Box width="full" height="1px" bgColor="neutral.lightest" />
      <HStack justifyContent="space-between" w="full">
        <Stack w="2/9">
          <VStack space="stack.nano" justifyContent="space-between">
            <UI.Text color="neutral.black" variant="semibold">
              Ultimo preço
            </UI.Text>
            <AssetValueWithPercentage
              value={offer.lastPriceNormalized}
              percentage={offer.priceChangeTaxValueNormalized}
              iconColor={offer.priceChangeTaxColorNormalized}
              updateDate={offer.tradeDateNormalized}
            />
          </VStack>
        </Stack>
        <Stack w="2/9">
          <VStack space="stack.nano" justifyContent="space-between">
            <UI.Text color="neutral.black" variant="semibold">
              Melhor Compra
            </UI.Text>
            <TableContentItemText>{offer.bidPriceNormalized}</TableContentItemText>
          </VStack>
        </Stack>
        <Stack w="2/9">
          <VStack space="stack.nano" justifyContent="space-between">
            <UI.Text color="neutral.black" variant="semibold">
              Melhor Venda
            </UI.Text>
            <TableContentItemText>{offer.askPriceNormalized}</TableContentItemText>
          </VStack>
        </Stack>
        <Stack w="1/6">
          <VStack space="stack.nano" justifyContent="space-between">
            <UI.Text color="neutral.black" variant="semibold">
              Fechamento
            </UI.Text>
            <TableContentItemText>{offer.closePriceNormalized}</TableContentItemText>
          </VStack>
        </Stack>
        <Stack w="1/7"></Stack>
      </HStack>
      <HStack justifyContent="space-between" w="full" alignItems="center">
        <Stack w="2/9">
          <VStack space="stack.quarck" justifyContent="space-between">
            <UI.Text color="neutral.black" variant="semibold">
              Tipo da ordem
            </UI.Text>
            <TableContentItemText>Limitada</TableContentItemText>
          </VStack>
        </Stack>
        <Stack w="2/9">
          <VStack space="stack.quarck" justifyContent="space-between">
            <UI.Text color="neutral.black" variant="semibold">
              Quantidade
            </UI.Text>
            <Stack pr="inline.xxxs">
              <UI.InputStepper
                stepperValue={cautionStepper}
                max={availableAmount}
                min={fractionalLotSize}
                name={`equity-amount-order-input${id}`}
                keyboardType="numeric"
                defaultValue={amountItem}
                onChange={handleChangeAmountItem}
                showErrorMessage={false}
                onFocus={() => setIsFocusedAmountInput(true)}
                onBlur={() => setIsFocusedAmountInput(false)}
                {...(isFocusedAmountInput &&
                  offer.saleAvailableAmount && {
                    helpText: `Quantidade disp.: ${offer.saleAvailableAmount}`,
                  })}
                {...(totalPriceIsInvalid && {
                  customError: 'Quantidade inválida',
                })}
              />
            </Stack>
          </VStack>
        </Stack>
        <Stack w="2/9">
          <VStack space="stack.quarck" justifyContent="space-between">
            <UI.Text color="neutral.black" variant="semibold">
              Preço por ativo
            </UI.Text>
            <Stack pr="inline.xxxs">
              <UI.InputStepper
                stepperValue={0.01}
                max={1000000}
                min={0.01}
                fontSize={Typography['fontsize.md']}
                keyboardType="numeric"
                ignoreStepperDivision
                maskOption="money"
                name={`equity-amount-price${id}`}
                testId={`equity-amount-price${id}`}
                defaultValue={priceItem || 0.01}
                onChange={handleChangePriceItem}
                showErrorMessage={false}
                disabled={isSelected}
                {...((totalPrice === 0 || totalPrice > 1000000) && {
                  customError: 'Valor inválido',
                })}
              />
            </Stack>
          </VStack>
        </Stack>
        <Stack w="1/6">
          <VStack space="stack.quarck" justifyContent="space-between">
            <UI.Text color="neutral.black" variant="semibold">
              Total estimado
            </UI.Text>
            <TableContentItemText>{Utils.Mask.Money(totalPrice)}</TableContentItemText>
          </VStack>
        </Stack>
        <Stack w="1/7">
          <Box w="100%">
            <Button
              id={selectedTab.typeGetterId}
              offer={completeOffer}
              isDisabled={totalPriceIsInvalid}
              productContextConstant={selectedTab}
            />
          </Box>
        </Stack>
      </HStack>
    </TableContent>
  );
};
